import { ConfigurationComparisonListResponse, ConfigurationComparisonDTO, ComparisonResultData, ConfigurationUploadListResponse, EnvironmentFileDTO, EnvironmentUploadDTO, BulkComparePayload, SasUrlResponse, ConfirmSasUploadPayload, ConfirmSasUploadResponse, ZipStructureResponse } from '../infrastructure/models';
import ApiService from './ApiService';
import api from './api';
import { toCamelCaseDeep } from '../utils/jsonTransform';

export const fetchConfigurationComparisons = async (
    tenantId: string,
    offset = 0,
    limit = 10,
    signal?: AbortSignal
): Promise<ConfigurationComparisonListResponse> => {
    const response = await ApiService.get<ConfigurationComparisonListResponse>({
        url: `tenants/${tenantId}/envcomparison/recent?hoursBack=168&offset=${offset}&limit=${limit}`,
        signal,
    });
    return response;
};

export const fetchComparisonDetail = async (
    tenantId: string,
    comparisonId: string,
    signal?: AbortSignal,
): Promise<ConfigurationComparisonDTO> => {
    return ApiService.get<ConfigurationComparisonDTO>({
        url: `tenants/${tenantId}/envcomparison/${comparisonId}`,
        signal,
    });
};

export const fetchComparisonResultData = async (
    tenantId: string,
    comparisonId: string,
    signal?: AbortSignal,
): Promise<ComparisonResultData | null> => {
    const response = await api.get(
        `tenants/${tenantId}/envcomparison/${comparisonId}/result-data`,
        { signal, timeout: 300_000 },
    );
    if (!response.data) return null;
    return toCamelCaseDeep(response.data) as ComparisonResultData;
};

export const fetchConfigurationUploads = async (
    tenantId: string,
    offset = 0,
    limit = 10,
    signal?: AbortSignal
): Promise<ConfigurationUploadListResponse> => {
    const response = await ApiService.get<ConfigurationUploadListResponse>({
        url: `tenants/${tenantId}/envcomparison/uploads?offset=${offset}&limit=${limit}`,
        signal,
    });
    return response;
};

/**
 * @deprecated Use {@link uploadConfigurationFiles} instead. SAS URL endpoints
 * are deprecated — all uploads should go through the backend (multipart).
 */
export const fetchSasUrl = async (
    tenantId: string,
    fileName: string,
    signal?: AbortSignal
): Promise<SasUrlResponse> => {
    const response = await ApiService.get<SasUrlResponse>({
        url: `tenants/${tenantId}/uploads/sas-url?fileName=${encodeURIComponent(fileName)}`,
        signal,
    });
    return response;
};

/**
 * @deprecated Use {@link uploadConfigurationFiles} instead. Direct browser-to-blob
 * uploads are blocked by the storage account firewall.
 */
export const uploadFileToSasUrl = async (
    sasUrl: string,
    file: File,
    signal?: AbortSignal
): Promise<void> => {
    const response = await fetch(sasUrl, {
        method: 'PUT',
        headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
        signal,
    });
    if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
};

/**
 * @deprecated Use {@link uploadConfigurationFiles} instead. SAS URL endpoints
 * are deprecated — all uploads should go through the backend (multipart).
 */
export const confirmSasUpload = async (
    tenantId: string,
    payload: ConfirmSasUploadPayload,
    signal?: AbortSignal,
): Promise<ConfirmSasUploadResponse> => {
    const response = await ApiService.post<ConfirmSasUploadResponse>({
        url: `tenants/${tenantId}/envcomparison/uploads/confirm-sas`,
        data: payload,
        signal,
    });
    return response;
};

export const uploadConfigurationFiles = async (
    tenantId: string,
    environmentId: string,
    name: string,
    description: string,
    files: File[],
    signal?: AbortSignal,
): Promise<void> => {
    const formData = new FormData();
    formData.append('environmentId', environmentId);
    formData.append('name', name);
    if (description) {
        formData.append('description', description);
    }
    files.forEach(file => formData.append('files', file));

    await api.post(`tenants/${tenantId}/envcomparison/upload`, formData, {
        signal,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300_000,
    });
};

export const fetchUploadsByEnvironment = async (
    tenantId: string,
    environmentId: string,
    signal?: AbortSignal,
): Promise<EnvironmentUploadDTO[]> => {
    const response = await ApiService.get<EnvironmentUploadDTO[]>({
        url: `tenants/${tenantId}/envcomparison/uploads/by-environment?environmentId=${encodeURIComponent(environmentId)}`,
        signal,
    });
    return response;
};

export const fetchEnvironmentFiles = async (
    tenantId: string,
    environmentId: string,
    signal?: AbortSignal,
): Promise<EnvironmentFileDTO[]> => {
    const response = await ApiService.get<EnvironmentFileDTO[]>({
        url: `tenants/${tenantId}/envcomparison/environment-files?environmentId=${environmentId}`,
        signal,
    });
    return response;
};

export const fetchZipStructure = async (
    tenantId: string,
    environmentId: string,
    zipBlobName: string,
    signal?: AbortSignal,
): Promise<ZipStructureResponse> => {
    const response = await ApiService.get<ZipStructureResponse>({
        url: `tenants/${tenantId}/envcomparison/zip-structure?environmentId=${environmentId}&zipBlobName=${encodeURIComponent(zipBlobName)}`,
        signal,
    });
    return response;
};

export const bulkCompare = async (
    tenantId: string,
    payload: BulkComparePayload,
    signal?: AbortSignal,
): Promise<void> => {
    await ApiService.post({
        url: `tenants/${tenantId}/envcomparison`,
        data: payload,
        signal,
    });
};

export const runComparison = async (
    tenantId: string,
    comparisonId: string,
    signal?: AbortSignal,
): Promise<void> => {
    await ApiService.post({
        url: `tenants/${tenantId}/envcomparison/${comparisonId}/run`,
        signal,
    });
};

export const deleteConfigurationUpload = async (
  tenantId: string,
  uploadId: string,
  signal?: AbortSignal,
): Promise<void> => {
  await ApiService.delete({
    url: `tenants/${tenantId}/envcomparison/uploads/${uploadId}`,
    signal,
  });
};

export const deleteConfigurationComparison = async (
  tenantId: string,
  comparisonId: string,
  signal?: AbortSignal,
): Promise<void> => {
  await ApiService.delete({
    url: `tenants/${tenantId}/envcomparison/${comparisonId}`,
    signal,
  });
};